import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axiosInstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";

const AddEvent = () => {
  const { t, i18n } = useTranslation();
  const languageDirection = i18n.language === "ar" ? "rtl" : "ltr";

  const [formData, setFormData] = useState({
    englishName: "",
    arabicName: "",
    englishDetails: "",
    arabicDetails: "",
    eventOn: "",
    eventEndOn: "",
    hoursDuration: "",
    link: "",
    bannerUrl: "",
    language: 1,
    isExternalEvent: false,
    isOnline: false,
    cityId: "",
    countryId: "",
    street: "",
    eventSpecialities: [],
    isEditMode: false,
  });

  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [displayedSpecialities, setDisplayedSpecialities] = useState([]);

  const [loading, setLoading] = useState(false);
  const [searchCountry, setSearchCountry] = useState("");
  const [searchCity, setSearchCity] = useState("");
  const [filteredCountries, setFilteredCountries] = useState(countries.items);
  const [filteredCities, setFilteredCities] = useState(cities.items);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (searchCountry) {
      const filtered = countries.items.filter((country) =>
        (i18n.language === "ar" ? country.arabicName : country.englishName)
          .toLowerCase()
          .includes(searchCountry.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries.items);
    }
  }, [searchCountry, countries.items]);

  // تصفية المدن بناءً على النص المدخل في حقل البحث
  useEffect(() => {
    if (searchCity) {
      const filtered = cities.items.filter((city) =>
        (i18n.language === "ar" ? city.arabicName : city.englishName)
          .toLowerCase()
          .includes(searchCity.toLowerCase())
      );
      setFilteredCities(filtered);
    } else {
      setFilteredCities(cities.items);
    }
  }, [searchCity, cities.items]);

  // جلب المدن بناءً على البلد المختار
  useEffect(() => {
    const fetchCities = async () => {
      if (formData.countryId) {
        try {
          const citiesResponse = await axiosInstance.get(
            `/api/v1/lookups/cities?countryId=${formData.countryId}`
          );
          setCities(citiesResponse.data); // تحديث قائمة المدن
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      } else {
        setCities([]);
      }
    };

    fetchCities();
  }, [formData.countryId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch countries
        const countriesResponse = await axiosInstance.get(
          "/api/v1/lookups/countries"
        );
        setCountries(countriesResponse.data);

        // Fetch specialities
        const specialitiesResponse = await axiosInstance.get(
          "/api/v1/lookups/specialities"
        );
        setSpecialities(specialitiesResponse.data);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked, files, options } = e.target;
    if (type === "select-multiple") {
      const selectedValues = Array.from(options)
        .filter((option) => option.selected)
        .map((option) => option.value);

      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: selectedValues,
      }));
    } else if (type === "checkbox") {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: checked,
      }));
    } else if (type === "file") {
      const file = files[0];
      if (file) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          [name]: file,
          [`${name}Preview`]: URL.createObjectURL(file),
        }));
      }
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value || "",
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formDataToSend = new FormData();
    e.preventDefault();
    console.log("Submitting...");

    formDataToSend.append("englishName", formData.englishName);
    formDataToSend.append("arabicName", formData.arabicName);
    formDataToSend.append("englishDetails", formData.englishDetails);
    formDataToSend.append("arabicDetails", formData.arabicDetails);
    formDataToSend.append("eventOn", formData.eventOn);
    formDataToSend.append("eventEndOn", formData.eventEndOn);

    const hoursDuration = parseFloat(formData.hoursDuration);
    formDataToSend.append("hoursDuration", hoursDuration);
    formDataToSend.append("bannerUrl", formData.bannerUrl);
    formDataToSend.append("eventSpecialities", formData.eventSpecialities);

    const language = parseInt(formData.language);
    formDataToSend.append("language", language);

    formDataToSend.append("isOnline", formData.isOnline);
    formDataToSend.append("cityId", formData.cityId);
    formDataToSend.append("countryId", formData.countryId);
    formDataToSend.append("street", formData.street);
    formDataToSend.append("isEditMode", formData.isEditMode);

    console.log("FormData final:", formDataToSend);

    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/api/v1/speakers/events",
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const successMessage = response.data?.message || t("successMessages");
      toast.success(successMessage);

      setTimeout(() => {
        window.location.href = `/organizer-events`;
      }, 3000);
    } catch (error) {
      const errorMessage =
        error.detail ||
        error.title ||
        t("messages.errorMessages.unexpectedError");

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // تصفية الدول بناءً على النص المدخل في حقل البحث
    if (searchCountry) {
      const filtered = countries.items.filter((country) =>
        (i18n.language === "ar" ? country.arabicName : country.englishName)
          .toLowerCase()
          .includes(searchCountry.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries.items);
    }
  }, [searchCountry, countries.items]);

  // جلب القطاعات
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const response = await axiosInstance.get("/api/v1/lookups/industries");
        setIndustries(response.data.items);
      } catch (error) {
        console.error("Error fetching industries:", error);
      }
    };

    fetchIndustries();
  }, []);

  // جلب التخصصات بناءً على القطاع المختار
  useEffect(() => {
    const fetchSpecialities = async () => {
      if (selectedIndustry) {
        try {
          const response = await axiosInstance.get(
            `/api/v1/lookups/specialities?industryId=${selectedIndustry}`
          );
          setSpecialities(response.data.items);
          setDisplayedSpecialities(response.data.items);
        } catch (error) {
          console.error("Error fetching specialities:", error);
        }
      }
    };

    fetchSpecialities();
  }, [selectedIndustry]);

  const handleIndustryChange = async (selectedOption) => {
    setSelectedIndustry(selectedOption?.value || null);

    if (selectedOption) {
      try {
        const response = await axiosInstance.get(
          `/api/v1/lookups/specialities?industryId=${selectedOption.value}`
        );
        setSpecialities(response.data.items || []);
      } catch (error) {
        console.error("Error fetching specialities:", error);
        setSpecialities([]);
      }
    } else {
      setSpecialities([]); // إعادة تعيين القائمة عند عدم اختيار قطاع
    }
  };

  const handleSpecialityChange = (e, specialityId) => {
    const isChecked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      eventSpecialities: prev.eventSpecialities
        ? isChecked
          ? [...new Set([...prev.eventSpecialities, specialityId])]
          : prev.eventSpecialities.filter((id) => id !== specialityId)
        : [specialityId],
    }));
  };

  const toggleShowMore = () => {
    setShowMore((prevState) => !prevState);
  };

  const maxLength = 500; // الحد الأقصى للأحرف

  const getColorForTextArea = (textLength) => {
    const percentage = (textLength / maxLength) * 100;
    if (percentage < 50) return "green"; // أقل من 50% الأخضر
    if (percentage < 90) return "orange"; // أقل من 90% البرتقالي
    return "red"; // 90% أو أكثر الأحمر
  };

  const industryOptions = industries.map((industry) => ({
    value: industry.id,
    label: i18n.language === "ar" ? industry.arabicName : industry.englishName,
  }));

  return (
    <div dir={languageDirection}>
      <section className="py-5">
        <div className="container profile-edit-container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card shadow-sm">
                <div className="card-body p-4 p-lg-5">
                  <form onSubmit={handleSubmit} className="row g-4">
                    {/* Arabic Name */}
                    <div className="col-12 col-md-6">
                      <label htmlFor="arabicName" className="form-label">
                        {t("addEvent.arabicName")}
                      </label>
                      <input
                        id="arabicName"
                        name="arabicName"
                        type="text"
                        value={formData.arabicName}
                        onChange={handleChange}
                        placeholder={t("addEvent.arabicNamePlaceholder")}
                        className="form-control rounded-3"
                      />
                    </div>

                    {/* English Name */}
                    <div className="col-12 col-md-6">
                      <label htmlFor="englishName" className="form-label">
                        {t("addEvent.englishName")}
                      </label>
                      <input
                        id="englishName"
                        name="englishName"
                        type="text"
                        value={formData.englishName}
                        onChange={handleChange}
                        placeholder={t("addEvent.englishNamePlaceholder")}
                        className="form-control rounded-3"
                      />
                    </div>

                    {/* Arabic Details */}
                    <div className="col-12">
                      <label htmlFor="arabicDetails" className="form-label">
                        {t("addEvent.ArabicDetails")}
                      </label>
                      <textarea
                        id="arabicDetails"
                        name="arabicDetails"
                        rows="4"
                        value={formData.arabicDetails}
                        onChange={handleChange}
                        placeholder={t("addEvent.ArabicDetailsPlaceholder")}
                        className="form-control rounded-3"
                        style={{
                          borderColor: getColorForTextArea(
                            formData.arabicDetails?.length || 0
                          ),
                        }}
                      />
                      <small className="text-muted d-block mt-1">
                        {formData.arabicDetails?.length} / {maxLength}{" "}
                        {t("AddArticle.charactersRemaining")}
                      </small>
                    </div>

                    {/* English Details */}
                    <div className="col-12">
                      <label htmlFor="englishDetails" className="form-label">
                        {t("addEvent.EnglishDetails")}
                      </label>
                      <textarea
                        id="englishDetails"
                        name="englishDetails"
                        rows="4"
                        value={formData.englishDetails}
                        onChange={handleChange}
                        placeholder={t("addEvent.EnglishDetailsPlaceholder")}
                        className="form-control rounded-3"
                        style={{
                          borderColor: getColorForTextArea(
                            formData.englishDetails?.length || 0
                          ),
                        }}
                      />
                      <small className="text-muted d-block mt-1">
                        {formData.englishDetails?.length} / {maxLength}{" "}
                        {t("AddArticle.charactersRemaining")}
                      </small>
                    </div>

                    {/* Event Dates */}
                    <div className="col-md-6">
                      <label htmlFor="eventOn" className="form-label">
                        {t("addEvent.startDate")}
                      </label>
                      <input
                        id="eventOn"
                        name="eventOn"
                        type="datetime-local"
                        value={formData.eventOn}
                        onChange={handleChange}
                        className="form-control rounded-3"
                      />
                      <small className="form-text text-muted">
                        <i className="feather-info me-1"></i>{" "}
                        {t("eventDateTime")}
                      </small>
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="eventEndOn" className="form-label">
                        {t("addEvent.endDate")}
                      </label>
                      <input
                        id="eventEndOn"
                        name="eventEndOn"
                        type="datetime-local"
                        value={formData.eventEndOn}
                        onChange={handleChange}
                        className="form-control rounded-3"
                      />
                    </div>

                    {/* Duration */}
                    <div className="col-md-6">
                      <label htmlFor="hoursDuration" className="form-label">
                        {t("addEvent.duration")}
                      </label>
                      <input
                        id="hoursDuration"
                        name="hoursDuration"
                        type="number"
                        step="0.1"
                        value={formData.hoursDuration}
                        onChange={handleChange}
                        placeholder={t("addEvent.durationPlaceholder")}
                        className="form-control rounded-3"
                      />
                    </div>

                    {/* Language */}
                    <div className="col-md-6">
                      <label htmlFor="language" className="form-label">
                        {t("addEvent.language")}
                      </label>
                      <select
                        id="language"
                        name="language"
                        value={formData.language}
                        onChange={handleChange}
                        className="form-select rounded-3"
                      >
                        <option value="">{t("addEvent.selectLanguage")}</option>
                        <option value="1">
                          {t("addEvent.languageArabic")}
                        </option>
                        <option value="2">
                          {t("addEvent.languageEnglish")}
                        </option>
                      </select>
                    </div>

                    <div className="rbt-course-field-wrapper rbt-default-form mt-5">
                      {/* Banner URL */}
                      <h6>{t("AddBook.coverImage")}</h6>
                      <div className="rbt-create-course-thumbnail upload-area">
                        <div className="upload-area">
                          <div
                            className="brows-file-wrapper"
                            style={{
                              backgroundColor: "white",
                              border: "2px dashed #e9ecef",
                              width: "100%",
                              height: "200px",
                              overflow: "hidden",
                            }}
                          >
                            <input
                              name="bannerUrl"
                              id="bannerUrl"
                              type="file"
                              className="inputfile"
                              onChange={handleChange}
                            />

                            <img
                              id="bannerUrl"
                              src={
                                formData.bannerUrlPreview
                                  ? formData.bannerUrlPreview
                                  : "assets/images/others/thumbnail-placeholder.svg"
                              }
                              alt="file image"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />

                            <label
                              className="d-flex"
                              htmlFor="bannerUrl"
                              title={t("AddBook.noFileSelected")}
                            >
                              <i className="feather-upload"></i>
                              <span className="text-center">
                                {t("AddBook.chooseImage")}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Is External Event */}
                      <div className="rbt-course-field-wrapper rbt-default-form mt-3">
                        <div className="rbt-checkbox-label mb--15">
                          <input
                            className="rbt-checkbox"
                            id="isExternalEvent"
                            name="isExternalEvent"
                            type="checkbox"
                            checked={formData.isExternalEvent}
                            onChange={handleCheckboxChange}
                          />
                          <label htmlFor="isExternalEvent">
                            {t("addEvent.isExternalEvent")}
                          </label>
                        </div>
                      </div>

                      {/* Event Link (Only for External Events) */}
                      {formData.isExternalEvent && (
                        <div className="col-12">
                          <label htmlFor="link" className="form-label">
                            {t("addEvent.eventLink")}
                          </label>
                          <input
                            id="link"
                            name="link"
                            type="text"
                            value={formData.link}
                            onChange={handleChange}
                            placeholder={t("addEvent.eventLinkPlaceholder")}
                            className="form-control rounded-3"
                          />
                        </div>
                      )}

                      {/* Is Online */}
                      <div className="rbt-checkbox-label mt-3">
                        <input
                          className="rbt-checkbox"
                          id="isOnline"
                          name="isOnline"
                          type="checkbox"
                          checked={formData.isOnline}
                          onChange={handleCheckboxChange}
                        />
                        <label htmlFor="isOnline">
                          {t("addEvent.isOnline")}
                        </label>
                      </div>

                      {/* Event Link (Only if not online) */}
                      {formData.isOnline && (
                        <div className="col-12">
                          <label htmlFor="link" className="form-label">
                            {t("addEvent.eventLink")}
                          </label>
                          <input
                            id="link"
                            name="link"
                            type="text"
                            value={formData.link}
                            onChange={handleChange}
                            placeholder={t("addEvent.eventLinkPlaceholder")}
                            className="form-control rounded-3"
                          />
                        </div>
                      )}

                      {/* Country (Only if not online) */}
                      {!formData.isOnline && (
                        <div className="col-12">
                          <label htmlFor="countryId" className="form-label">
                            {t("country")}
                          </label>

                          {/* حقل الدول */}
                          <select
                            name="countryId"
                            value={formData.countryId || ""}
                            onChange={handleChange}
                            className="form-control rounded-3"
                          >
                            <option value="">{t("select_country")}</option>
                            {Array.isArray(filteredCountries) &&
                              filteredCountries.map((country) => (
                                <option key={country.id} value={country.id}>
                                  {i18n.language === "ar"
                                    ? country.arabicName
                                    : country.englishName}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      {/* City (Only if not online) */}
                      {!formData.isOnline && formData.countryId && (
                        <div className="col-12">
                          <label htmlFor="cityId" className="form-label">
                            {t("city")}
                          </label>
                          <select
                            name="cityId"
                            value={formData.cityId || ""}
                            onChange={handleChange}
                            className="form-control rounded-3"
                          >
                            <option value="">{t("select_city")}</option>
                            {cities &&
                              cities.items &&
                              Array.isArray(cities.items) &&
                              cities.items.map((city) => (
                                <option key={city.id} value={city.id}>
                                  {i18n.language === "ar"
                                    ? city.arabicName
                                    : city.englishName}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      {/* Street (Only if not online) */}
                      {!formData.isOnline && (
                        <div className="col-12">
                          <label htmlFor="street" className="form-label">
                            {t("addEvent.street")}
                          </label>
                          <input
                            id="street"
                            name="street"
                            type="text"
                            value={formData.street}
                            onChange={handleChange}
                            placeholder={t("addEvent.streetPlaceholder")}
                            className="form-control rounded-3"
                          />
                        </div>
                      )}

                      {/* القطاعات والتحصصات */}
                      <div className="rbt-form-group mt-3">
                        <div className="row">
                          {/* قائمة القطاعات مع Auto-complete */}
                          <div className="col-lg-6 col-md-6">
                            <label>{t("select_industry")}</label>
                            <Select
                              options={industryOptions}
                              onChange={(selectedOption) =>
                                handleIndustryChange(selectedOption)
                              }
                              placeholder={t("select_industry")}
                              isClearable
                            />
                          </div>

                          {/* قائمة التخصصات */}
                          <div className="col-lg-6 col-md-6">
                            <div className="specialities-list">
                              <label>{t("addEvent.specialitiesList")}</label>

                              <div className="specialities-list">
                                {selectedIndustry ? (
                                  displayedSpecialities.length > 0 ? (
                                    displayedSpecialities.map((speciality) => (
                                      <div
                                        key={speciality.id}
                                        className="form-check"
                                      >
                                        <input
                                          type="checkbox"
                                          id={`speciality-${speciality.id}`}
                                          checked={formData.eventSpecialities.includes(
                                            speciality.id
                                          )}
                                          onChange={(e) =>
                                            handleSpecialityChange(
                                              e,
                                              speciality.id
                                            )
                                          }
                                          className="rbt-checkbox"
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`speciality-${speciality.id}`}
                                        >
                                          {i18n.language === "ar"
                                            ? speciality.arabicName
                                            : speciality.englishName}
                                        </label>
                                      </div>
                                    ))
                                  ) : (
                                    <p>
                                      {t("addEvent.noSpecialitiesAvailable")}
                                    </p>
                                  )
                                ) : (
                                  <p>{t("addEvent.noSpecialitiesAvailable")}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="mt--10 row g-5">
                      <div className="col-lg-12">
                        <button className="btn btn-primary w-100" type="submit">
                          <span className="icon-reverse-wrapper">
                            <span className="btn-text">
                              {t("addEvent.submitButton")}
                            </span>
                          </span>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ToastContainer />
    </div>
  );
};

export default AddEvent;
